import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { countriesService, type Country, type CountryState } from '../../services/countries';

interface CountryStateFieldsProps {
  /** ISO 3166-1 alpha-2, or '' for none chosen yet. */
  country: string;
  /** Subdivision code within `country`, or '' for none. */
  state: string;
  onChange: (patch: { country?: string; state?: string }) => void;
}

/** Radix Select treats '' as "no value", so the clear option carries a token. */
const NO_STATE = '__none__';

/**
 * The country and state pair of a location's address. The state list follows
 * the country and is empty for the many countries ISO records no subdivisions
 * for — the field hides itself rather than showing an empty dropdown.
 *
 * Changing the country clears the state, because a subdivision code only means
 * anything inside its own country; the server enforces the same pairing.
 */
export function CountryStateFields({ country, state, onChange }: CountryStateFieldsProps) {
  const { t } = useTranslation();
  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<CountryState[]>([]);

  useEffect(() => {
    let active = true;
    countriesService
      .getCountries()
      .then((list) => { if (active) setCountries(list); })
      .catch(() => { if (active) setCountries([]); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!country) {
      setStates([]);
      return;
    }
    let active = true;
    countriesService
      .getStates(country)
      .then((list) => { if (active) setStates(list); })
      .catch(() => { if (active) setStates([]); });
    return () => { active = false; };
  }, [country]);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="country">{t('locationInfo.country')}</Label>
        <Select
          value={country || undefined}
          onValueChange={(value) => onChange({ country: value, state: '' })}
        >
          <SelectTrigger id="country">
            <SelectValue placeholder={t('locationInfo.countryPlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            {countries.map((c) => (
              <SelectItem key={c.code} value={c.code}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {states.length > 0 && (
        <div className="space-y-2">
          <Label htmlFor="state">{t('locationInfo.state')}</Label>
          <Select
            value={state || NO_STATE}
            onValueChange={(value) => onChange({ state: value === NO_STATE ? '' : value })}
          >
            <SelectTrigger id="state">
              <SelectValue placeholder={t('locationInfo.statePlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_STATE}>{t('locationInfo.statePlaceholder')}</SelectItem>
              {states.map((s) => (
                <SelectItem key={s.code} value={s.code}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}
